import { useState, useCallback, useEffect } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  doc,
  getDoc,
  setDoc,
  Timestamp
} from 'firebase/firestore';
import { signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../utils/firebase';
import {
  CannCompoundingSurveyData,
  SurveyResponse,
  AggregatedAnalytics,
  APIResponse,
  ExportFilter
} from '../types/survey.types';

export const useFirebase = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Handle authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
    });

    return () => unsubscribe();
  }, []);

  // Ensure user is authenticated (sign in anonymously if needed)
  const ensureAuthenticated = useCallback(async (): Promise<boolean> => {
    try {
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }
      return true;
    } catch (err) {
      console.error('Authentication error:', err);
      setError(err instanceof Error ? err.message : 'Authentication failed');
      return false;
    }
  }, []);

  const hashIpAddress = useCallback((ip: string): string => {
    // Simple hash function for IP address (for privacy)
    let hash = 0;
    for (let i = 0; i < ip.length; i++) {
      const char = ip.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }, []);

  const getClientIP = useCallback(async (): Promise<string> => {
    // In production, you might use a service to get the real IP
    // For now, we'll use a placeholder
    return 'client-ip-placeholder';
  }, []);

  const updateAggregatedAnalytics = useCallback(async (surveyData: any) => {
    try {
      const analyticsRef = doc(db, 'analytics', 'aggregated');
      const analyticsDoc = await getDoc(analyticsRef);

      let currentAnalytics: AggregatedAnalytics;

      if (analyticsDoc.exists()) {
        currentAnalytics = analyticsDoc.data() as AggregatedAnalytics;
      } else {
        // Initialize analytics if it doesn't exist
        currentAnalytics = {
          meanAge: 0,
          meanRating: 0,
          meanWeeksOfUse: 0,
          genderDistribution: {},
          conditionFrequency: {},
          totalResponses: 0,
          lastUpdated: Timestamp.now()
        };
      }

      const newTotalResponses = currentAnalytics.totalResponses + 1;

      // Update means using incremental calculation
      const newMeanAge = (
        (currentAnalytics.meanAge * currentAnalytics.totalResponses) + surveyData.patientData.age
      ) / newTotalResponses;

      const newMeanRating = (
        (currentAnalytics.meanRating * currentAnalytics.totalResponses) + surveyData.feedback.satisfactionRating
      ) / newTotalResponses;

      const newMeanWeeksOfUse = (
        (currentAnalytics.meanWeeksOfUse * currentAnalytics.totalResponses) + surveyData.clinicalData.weeksOfUse
      ) / newTotalResponses;

      // Update gender distribution
      const newGenderDistribution = { ...currentAnalytics.genderDistribution };
      const gender = surveyData.patientData.gender;
      newGenderDistribution[gender] = (newGenderDistribution[gender] || 0) + 1;

      // Update condition frequency
      const newConditionFrequency = { ...currentAnalytics.conditionFrequency };
      surveyData.clinicalData.conditions.forEach((condition: string) => {
        newConditionFrequency[condition] = (newConditionFrequency[condition] || 0) + 1;
      });

      // Update or create analytics document
      const updatedAnalytics = {
        meanAge: newMeanAge,
        meanRating: newMeanRating,
        meanWeeksOfUse: newMeanWeeksOfUse,
        genderDistribution: newGenderDistribution,
        conditionFrequency: newConditionFrequency,
        totalResponses: newTotalResponses,
        lastUpdated: serverTimestamp()
      };

      // Use setDoc to create or update the analytics document
      await setDoc(analyticsRef, updatedAnalytics);

    } catch (err) {
      console.error('Error updating analytics:', err);
      // Don't throw - analytics update shouldn't fail the survey submission
    }
  }, []);

  const submitSurvey = useCallback(async (
    surveyData: CannCompoundingSurveyData
  ): Promise<APIResponse<string>> => {
    setLoading(true);
    setError(null);

    try {
      // Ensure user is authenticated before attempting to submit
      const isAuth = await ensureAuthenticated();
      if (!isAuth) {
        setLoading(false);
        return {
          success: false,
          error: 'Authentication failed. Please try again.'
        };
      }

      const clientIP = await getClientIP();
      const hashedIP = hashIpAddress(clientIP);

      // Structure the data according to the database schema
      const structuredData = {
        metadata: {
          submittedAt: serverTimestamp(),
          surveyVersion: '1.0',
          ipAddress: hashedIP,
        },
        patientData: {
          initials: surveyData.patientInitials.toUpperCase(),
          age: surveyData.age,
          gender: surveyData.gender,
          productName: surveyData.productName,
        },
        clinicalData: {
          conditions: surveyData.clinicalConditions,
          otherCondition: surveyData.otherCondition || '',
          weeksOfUse: surveyData.weeksOfUse,
          previousUse: surveyData.previousCannabisUse,
          previousProducts: surveyData.previousProducts || '',
        },
        feedback: {
          satisfactionRating: surveyData.satisfactionRating,
          sideEffects: surveyData.sideEffects || '',
          additionalFeedback: surveyData.additionalFeedback || '',
        },
      };

      // Submit to Firestore
      const docRef = await addDoc(
        collection(db, 'surveys', 'cannCompoundingQA', 'responses'),
        structuredData
      );

      // Update aggregated analytics
      await updateAggregatedAnalytics(structuredData);

      setLoading(false);
      return {
        success: true,
        data: docRef.id,
        message: 'Survey submitted successfully'
      };

    } catch (err) {
      console.error('Survey submission error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit survey');
      setLoading(false);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to submit survey'
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashIpAddress, getClientIP, ensureAuthenticated]);

  const getSurveyResponses = useCallback(async (
    filters?: ExportFilter
  ): Promise<APIResponse<SurveyResponse[]>> => {
    setLoading(true);
    setError(null);

    try {
      console.log('[useFirebase] Getting survey responses from new responses collection');

      // Query the NEW responses collection (top-level, not nested)
      let queryRef = collection(db, 'responses');

      // Build query with filters
      let constraints = [];

      if (filters?.dateRange) {
        constraints.push(
          where('metadata.submittedAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
          where('metadata.submittedAt', '<=', Timestamp.fromDate(filters.dateRange.end))
        );
      }

      if (filters?.satisfactionRating && filters.satisfactionRating.length > 0) {
        constraints.push(
          where('feedback.satisfactionRating', 'in', filters.satisfactionRating)
        );
      }

      // Add ordering
      constraints.push(orderBy('metadata.submittedAt', 'desc'));

      const q = query(queryRef, ...constraints);
      const querySnapshot = await getDocs(q);

      console.log('[useFirebase] Query completed, found', querySnapshot.size, 'responses');

      const responses: SurveyResponse[] = [];
      querySnapshot.forEach((doc) => {
        responses.push({
          id: doc.id,
          ...doc.data() as Omit<SurveyResponse, 'id'>
        });
      });

      // Apply client-side filters that can't be done in Firestore
      let filteredResponses = responses;

      if (filters?.ageRange) {
        filteredResponses = filteredResponses.filter(response =>
          response.patientData.age >= filters.ageRange!.min &&
          response.patientData.age <= filters.ageRange!.max
        );
      }

      if (filters?.gender && filters.gender.length > 0) {
        filteredResponses = filteredResponses.filter(response =>
          filters.gender!.includes(response.patientData.gender)
        );
      }

      if (filters?.productType) {
        filteredResponses = filteredResponses.filter(response =>
          response.patientData.productName.toLowerCase().includes(filters.productType!.toLowerCase())
        );
      }

      console.log('[useFirebase] Returning', filteredResponses.length, 'filtered responses');

      setLoading(false);
      return {
        success: true,
        data: filteredResponses,
        message: `Retrieved ${filteredResponses.length} survey responses`
      };

    } catch (err) {
      console.error('[useFirebase] Error fetching survey responses:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch survey responses');
      setLoading(false);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to fetch survey responses'
      };
    }
  }, []);

  const getAnalytics = useCallback(async (): Promise<APIResponse<AggregatedAnalytics>> => {
    setLoading(true);
    setError(null);

    try {
      const analyticsRef = doc(db, 'analytics', 'aggregated');
      const analyticsDoc = await getDoc(analyticsRef);

      if (!analyticsDoc.exists()) {
        setLoading(false);
        return {
          success: true,
          data: {
            meanAge: 0,
            meanRating: 0,
            meanWeeksOfUse: 0,
            genderDistribution: {},
            conditionFrequency: {},
            totalResponses: 0,
            lastUpdated: Timestamp.now()
          },
          message: 'No analytics data available yet'
        };
      }

      const analyticsData = analyticsDoc.data() as AggregatedAnalytics;

      setLoading(false);
      return {
        success: true,
        data: analyticsData,
        message: 'Analytics retrieved successfully'
      };

    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
      setLoading(false);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to fetch analytics'
      };
    }
  }, []);

  const createProduct = useCallback(async (
    name: string,
    category: string
  ): Promise<APIResponse<string>> => {
    setLoading(true);
    setError(null);

    try {
      const productData = {
        name,
        category,
        active: true,
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'products'), productData);

      setLoading(false);
      return {
        success: true,
        data: docRef.id,
        message: 'Product created successfully'
      };

    } catch (err) {
      console.error('Error creating product:', err);
      setError(err instanceof Error ? err.message : 'Failed to create product');
      setLoading(false);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to create product'
      };
    }
  }, []);

  const getProducts = useCallback(async (): Promise<APIResponse<any[]>> => {
    setLoading(true);
    setError(null);

    try {
      const q = query(
        collection(db, 'products'),
        where('active', '==', true),
        orderBy('name')
      );

      const querySnapshot = await getDocs(q);
      const products: any[] = [];

      querySnapshot.forEach((doc) => {
        products.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setLoading(false);
      return {
        success: true,
        data: products,
        message: `Retrieved ${products.length} products`
      };

    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch products');
      setLoading(false);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to fetch products'
      };
    }
  }, []);

  return {
    loading,
    error,
    isAuthenticated,
    submitSurvey,
    getSurveyResponses,
    getAnalytics,
    createProduct,
    getProducts
  };
};

/**
 * Recalculate analytics for a survey
 * This is a standalone function to fix analytics after migrations or bug fixes
 */
export const recalculateAnalytics = async (surveyId: string): Promise<void> => {
  const recalculateAnalyticsFunction = httpsCallable(functions, 'recalculateAnalytics');
  await recalculateAnalyticsFunction({ surveyId });
};

export default useFirebase;